// frontend/src/components/staff/PublishScheduleModal.js
//
// Workforce — SCHED-SLICE1-E publish confirmation modal, wired to the
// live SCHED-SLICE1-C bulk-publish endpoint (PR #132 merged 2026-05-12).
//
// Visual spec: docs/design/workforce-scheduling-slice-1-design.md §6.2 +
// Scenario 12 + visual DoD §18.4.
//
// Renders BEFORE `POST /api/workforce/schedules/publish` fires, listing
// affected staff + per-staff shift count. The publish endpoint contract
// (request / response / error envelope) is defined in
// `controllers/workforceSchedulesController.js` and mirrored in
// `services/api.js#workforceShiftsAPI.publishSchedule`.
//
// Two-step posture:
//   1. Initial publish — server may return 400 PUBLISH_CONFIRM_REQUIRED
//      with conflicts. Modal re-renders inline conflict summary; admin
//      re-confirms with `confirmConflicts: true`.
//   2. Repeated publish — `confirmConflicts: true` flag is set on the
//      payload; server SHOULD succeed; if conflicts still fail, the
//      message flashes again (defensive UX).

import React from "react";
import { resolveStaffDisplayName } from "./scheduleBuilderUtils";
import "./PublishScheduleModal.css";

const PublishScheduleModal = ({
  open,
  weekRangeLabel,
  draftShifts,
  staffById,
  pendingConflicts,
  onConfirm,
  onCancel,
  isSubmitting,
  errorMessage,
}) => {
  const [confirmAcknowledged, setConfirmAcknowledged] = React.useState(false);

  // Esc closes (a11y).
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && typeof onCancel === "function" && !isSubmitting) {
        onCancel();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel, isSubmitting]);

  // Reset confirm-flag each fresh mount/open.
  React.useEffect(() => {
    if (open && !pendingConflicts) setConfirmAcknowledged(false);
  }, [open, pendingConflicts]);

  // Per-staff aggregation across draft shifts.
  const perStaffBreakdown = React.useMemo(() => {
    if (!Array.isArray(draftShifts)) return [];
    const groups = new Map();
    for (const shift of draftShifts) {
      if (!shift || shift.status !== "draft") continue;
      const key = String(shift.staffProfileId);
      if (!groups.has(key)) {
        const profile = staffById && staffById.get ? staffById.get(key) : null;
        groups.set(key, {
          staffProfileId: key,
          name: resolveStaffDisplayName(profile),
          count: 0,
        });
      }
      groups.get(key).count += 1;
    }
    return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [draftShifts, staffById]);

  if (!open) return null;

  const draftCount = Array.isArray(draftShifts)
    ? draftShifts.filter((s) => s && s.status === "draft").length
    : 0;
  const staffCount = perStaffBreakdown.length;
  const hasConflicts = !!pendingConflicts;

  const handleConfirm = () => {
    if (typeof onConfirm !== "function") return;
    onConfirm({ confirmConflicts: hasConflicts });
  };

  const confirmLabel = isSubmitting
    ? "Publishing..."
    : hasConflicts
      ? "Publish anyway"
      : "Publish";

  const confirmDisabled = isSubmitting || (hasConflicts && !confirmAcknowledged);

  return (
    <>
      <div
        className="publish-schedule-modal__backdrop"
        onClick={isSubmitting ? undefined : onCancel}
        aria-hidden="true"
        data-testid="publish-schedule-modal-backdrop"
      />
      <div
        className="publish-schedule-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="publish-schedule-modal-heading"
        data-testid="publish-schedule-modal"
      >
        <header className="publish-schedule-modal__header">
          <h2
            id="publish-schedule-modal-heading"
            className="publish-schedule-modal__heading"
          >
            Publish week of {weekRangeLabel}?
          </h2>
          <button
            type="button"
            className="publish-schedule-modal__close"
            onClick={onCancel}
            disabled={isSubmitting}
            aria-label="Close publish modal"
            data-testid="publish-schedule-modal-close"
          >
            &times;
          </button>
        </header>

        <div className="publish-schedule-modal__body">
          {errorMessage && (
            <div
              className="publish-schedule-modal__error"
              role="alert"
              data-testid="publish-schedule-modal-error"
            >
              {errorMessage}
            </div>
          )}

          <p className="publish-schedule-modal__summary">
            You're publishing {draftCount} draft shift{draftCount === 1 ? "" : "s"}.
          </p>

          {staffCount > 0 ? (
            <>
              <p className="publish-schedule-modal__staff-label">
                Affected staff ({staffCount}):
              </p>
              <ul
                className="publish-schedule-modal__staff-list"
                data-testid="publish-schedule-modal-staff-list"
              >
                {perStaffBreakdown.map((row) => (
                  <li key={row.staffProfileId}>
                    <span className="publish-schedule-modal__staff-name">{row.name}</span>
                    <span className="publish-schedule-modal__staff-count">
                      {row.count} shift{row.count === 1 ? "" : "s"}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="publish-schedule-modal__note">
                Affected staff will receive a notification.
              </p>
            </>
          ) : (
            <p className="publish-schedule-modal__note">
              No drafts to publish.
            </p>
          )}

          {hasConflicts && (
            <div
              className="publish-schedule-modal__conflict-banner"
              role="alert"
              data-testid="publish-schedule-modal-conflicts"
            >
              <p className="publish-schedule-modal__conflict-title">
                Conflicts detected
              </p>
              <ul className="publish-schedule-modal__conflict-list">
                {Array.isArray(pendingConflicts.availability) &&
                  pendingConflicts.availability.map((c, i) => (
                    <li key={`avail-${i}`}>{c.message || "Availability conflict"}</li>
                  ))}
                {Array.isArray(pendingConflicts.weeklyHourLimit) &&
                  pendingConflicts.weeklyHourLimit.map((c, i) => (
                    <li key={`wh-${i}`}>{c.message || "Weekly hour cap exceeded"}</li>
                  ))}
                {!Array.isArray(pendingConflicts.weeklyHourLimit) &&
                  pendingConflicts.weeklyHourLimit && (
                    <li>{pendingConflicts.weeklyHourLimit.message || "Weekly hour cap exceeded"}</li>
                  )}
              </ul>
              <label className="publish-schedule-modal__ack">
                <input
                  type="checkbox"
                  checked={confirmAcknowledged}
                  onChange={(e) => setConfirmAcknowledged(e.target.checked)}
                  disabled={isSubmitting}
                  data-testid="publish-schedule-modal-ack"
                />
                <span>I've reviewed the conflicts and want to publish anyway.</span>
              </label>
            </div>
          )}
        </div>

        <footer className="publish-schedule-modal__footer">
          <button
            type="button"
            className="publish-schedule-modal__cancel"
            onClick={onCancel}
            disabled={isSubmitting}
            data-testid="publish-schedule-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="publish-schedule-modal__confirm"
            onClick={handleConfirm}
            disabled={confirmDisabled || draftCount === 0}
            data-testid="publish-schedule-modal-confirm"
          >
            {confirmLabel}
          </button>
        </footer>
      </div>
    </>
  );
};

export default PublishScheduleModal;
